<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoadingPrograms || isLoadingMappings">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/4">
          <base-skeleton-loader class="tw-h-3 tw-w-full" />
        </div>
        <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        <div class="tw-flex-grow tw-grid tw-grid-cols-3 tw-gap-4">
          <base-skeleton-loader class="tw-col-span-1 tw-h-3" />
          <base-skeleton-loader class="tw-col-span-1 tw-h-3" />
          <base-skeleton-loader class="tw-col-span-1 tw-h-3" />
        </div>
      </div>
    </div>
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div class="tw-grid tw-grid-cols-1 tw-gap-y-6">
        <div v-for="row in orderedUiRows" :key="row.name" class="tw-flex tw-items-center tw-gap-x-4 tw-border-b tw-pb-4">
          <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex-col">
              <div class="tw-flex-col tw-my-auto tw-font-normal">
                {{ row.name }}
              </div>
            </div>
            <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
          </div>

          <div :class="['tw-flex-grow tw-grid tw-gap-4', `tw-grid-cols-3`]">
            <div class="tw-col-span-1">
              <base-currency-input label="Provider Price" :int-value.sync="row.providerPriceCents" v-model="row.providerPriceDisplay" :precision="0" />
            </div>
            <div class="tw-col-span-1">
              <base-currency-input label="Price Floor" :int-value.sync="row.priceFloorCents" v-model="row.priceFloorDisplay" :precision="0" />
            </div>

            <div class="tw-col-span-1">
              <base-currency-input label="Ceiling Price" :int-value.sync="row.ceilingPriceCents" v-model="row.ceilingPriceDisplay" :precision="0" />
            </div>

            <div v-for="pgInput in row.programGroupInputs" :key="pgInput.businessProgramId" class="tw-col-span-1 mt-1">
              <base-currency-input
                :label="formatProgramGroupName(pgInput.programGroup)"
                :int-value.sync="pgInput.suggestedPriceCents"
                v-model="pgInput.suggestedPriceDisplay"
                :precision="0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-between">
        <base-button type="secondary" :loading="isSaving && isApplyToAllClicked" :disabled="isSaving && !isApplyToAllClicked" class="tw-w-auto" @click="save(true)"
          >Apply price suggestions to all locations
        </base-button>
        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-3" :disabled="isSaving" @click="hide()">
            Cancel
          </base-button>
          <base-button :loading="isSaving && !isApplyToAllClicked" class="tw-w-auto" :disabled="isSaving && isApplyToAllClicked" @click="save(false)">
            Save
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon } from "@/assets/svgs";
import { orderBy } from "lodash";

export default {
  name: "BusinessLocationCostMappingsSlideover",
  components: {
    ArrowRightIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingPrograms: false,
      isLoadingMappings: false,
      isSaving: false,
      isApplyToAllClicked: false,
      businessLocation: {},
      programsData: [],
      uiRows: [],
      programPricesMap: {},
      applyToAllLocations: false,
    };
  },
  computed: {
    title() {
      // Check if businessLocation and name exist before accessing
      return this.businessLocation && this.businessLocation.name ? `${this.businessLocation.name} Program Pricing` : "Program Pricing"; // Fallback title
    },
    orderedUiRows() {
      return orderBy(this.uiRows, ["name"], ["asc"]);
    },
  },
  methods: {
    // Helper to format cents to display (now handles 0 to empty string)
    formatCentsToDisplay(cents) {
      if (typeof cents !== "number" || Number.isNaN(cents) || cents === null || cents === undefined) {
        return 0; // Return 0 for invalid or empty inputs, for currency input consistency
      }
      return cents / 100;
    },

    async show(businessLocationItem) {
      this.resetData(); // Clear previous data first
      this.businessLocation = businessLocationItem;
      this.$refs.slideover.show();
      await this.fetchAndPrepareProgramData();
    },
    hide() {
      this.$refs.slideover.hide();
      this.resetData();
    },
    resetData() {
      this.isLoadingPrograms = false;
      this.isLoadingMappings = false;
      this.isSaving = false;
      this.isApplyToAllClicked = false;
      this.businessLocation = {};
      this.programsData = [];
      this.uiRows = [];
      this.programPricesMap = {};
    },

    async fetchAndPrepareProgramData() {
      try {
        await this.getPrograms();
        await this.getPricesMappings();
        this.applySavedPricesToProgramsData();
        this.buildUiRowsFromProgramsData();
      } catch (error) {
        console.error("Error fetching or preparing program data:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program data",
          message: "There was an issue fetching program pricing. Please try again.",
          error,
        });
      }
    },

    async getPrograms() {
      try {
        this.isLoadingPrograms = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=false`);

        this.programsData = response.data.map((program) => ({
          ...program,
          suggestedPriceCents: 0,
          suggestedPriceDisplay: 0,
          providerPriceCents: 0,
          providerPriceDisplay: 0,
          priceFloorCents: 0,
          priceFloorDisplay: 0,
          ceilingPriceCents: 0,
          ceilingPriceDisplay: 0,
        }));
      } catch (error) {
        console.error("Error fetching master programs:", error);
        throw error;
      } finally {
        this.isLoadingPrograms = false;
      }
    },

    async getPricesMappings() {
      try {
        this.isLoadingMappings = true;
        const url = `/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings/program-prices`;
        const response = await this.$axios.get(url);

        this.programPricesMap = {};

        if (Array.isArray(response.data)) {
          response.data.forEach((mapping) => {
            const programId = mapping.businessProgramId;

            if (!programId) {
              return; // Skip this mapping if it doesn't have an ID
            }
            const suggestedPrice = mapping.suggestedPriceCents || 0;
            const providerPrice = mapping.providerPriceCents || 0;
            const priceFloor = mapping.priceFloorCents || 0; // Fetch new field
            const ceilingPrice = mapping.ceilingPriceCents || 0; // Fetch new field

            this.programPricesMap[programId] = {
              suggestedPriceCents: suggestedPrice,
              providerPriceCents: providerPrice,
              priceFloorCents: priceFloor, // Store new field
              ceilingPriceCents: ceilingPrice, // Store new field
            };
          });
        }
      } catch (error) {
        console.error("Error fetching program price mappings:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to load program price mappings",
          message: "There was an issue fetching program pricing. Please try again.",
          error,
        });
        throw error;
      } finally {
        this.isLoadingMappings = false;
      }
    },

    applySavedPricesToProgramsData() {
      this.programsData.forEach((program) => {
        const savedPrices = this.programPricesMap[program.businessProgramId];
        if (savedPrices) {
          program.suggestedPriceCents = savedPrices.suggestedPriceCents;
          program.suggestedPriceDisplay = this.formatCentsToDisplay(savedPrices.suggestedPriceCents);
          program.providerPriceCents = savedPrices.providerPriceCents;
          program.providerPriceDisplay = this.formatCentsToDisplay(savedPrices.providerPriceCents);
          program.priceFloorCents = savedPrices.priceFloorCents;
          program.priceFloorDisplay = this.formatCentsToDisplay(savedPrices.priceFloorCents);
          program.ceilingPriceCents = savedPrices.ceilingPriceCents;
          program.ceilingPriceDisplay = this.formatCentsToDisplay(savedPrices.ceilingPriceCents);
        }
      });
    },

    buildUiRowsFromProgramsData() {
      const tempUiRows = {};

      this.programsData.forEach((program) => {
        if (!program.programGroup) return;

        if (!tempUiRows[program.name]) {
          tempUiRows[program.name] = {
            name: program.name,
            providerPriceCents: program.providerPriceCents,
            providerPriceDisplay: program.providerPriceDisplay,
            priceFloorCents: program.priceFloorCents,
            priceFloorDisplay: program.priceFloorDisplay,
            ceilingPriceCents: program.ceilingPriceCents,
            ceilingPriceDisplay: program.ceilingPriceDisplay,
            programGroupInputs: [],
          };
        }

        tempUiRows[program.name].programGroupInputs.push(program);
      });

      this.uiRows = Object.values(tempUiRows)
        .filter((row) => row.programGroupInputs.length > 0)
        .map((row) => {
          row.programGroupInputs = orderBy(row.programGroupInputs, ["programGroup"]);
          return row;
        });
    },

    syncPricesToProgramsData() {
      this.uiRows.forEach((uiRow) => {
        // These are the prices that apply across the whole row/program group
        const sharedProviderPrice = uiRow.providerPriceCents;
        const sharedPriceFloor = uiRow.priceFloorCents;
        const sharedCeilingPrice = uiRow.ceilingPriceCents;

        // Iterate through each individual program within this UI row's group
        uiRow.programGroupInputs.forEach((pgInput) => {
          // Find the corresponding program object in the main programsData array
          // This is essential because programsData is what we use to build the payload
          const programInProgramsData = this.programsData.find((p) => p.businessProgramId === pgInput.businessProgramId);

          if (programInProgramsData) {
            // Update the shared prices (Provider, Floor, Ceiling) for this specific programDetail
            // This ensures every program that belongs to this uiRow.name group gets these synced values
            programInProgramsData.providerPriceCents = sharedProviderPrice;
            programInProgramsData.priceFloorCents = sharedPriceFloor;
            programInProgramsData.ceilingPriceCents = sharedCeilingPrice;

            // Update the SUGGESTED PRICE, which comes from the individual pgInput
            programInProgramsData.suggestedPriceCents = pgInput.suggestedPriceCents;
          }
        });
      });
    },
    formatProgramGroupName(groupName) {
      if (!groupName) {
        return "";
      }
      const match = groupName.match(/^(\d+\s*-\s*|)(\s*)(.*)$/); // Matches "NUMBER - " or "NUMBER-" or similar
      if (match && match[3]) {
        return match[3]; // Returns the part after the hyphen and space
      }
      return groupName; // If no match, return original string
    },

    async save(isApplyToAllLocations) {
      this.isApplyToAllClicked = isApplyToAllLocations;
      this.isSaving = true;
      const updates = [];
      this.syncPricesToProgramsData();
      this.programsData.forEach((programDetail) => {
        updates.push({
          businessProgramId: programDetail.businessProgramId,
          suggestedPriceCents: programDetail.suggestedPriceCents * 100,
          providerPriceCents: programDetail.providerPriceCents * 100,
          priceFloorCents: programDetail.priceFloorCents * 100,
          ceilingPriceCents: programDetail.ceilingPriceCents * 100,
        });
      });
      try {
        await this.$axios.patch(`/businesses/${this.businessId}/locations/${this.businessLocation.businessLocationId}/mappings/program-prices`, {
          programs: updates,
          isApplyToAllLocations: isApplyToAllLocations,
        });

        this.hide();
        this.EventBus.$emit("GLOBAL_NOTIFY", {
          title: "Program Prices Updated",
          message: `Program(s) updated successfully.`,
        });
      } catch (error) {
        console.error("Error saving program prices:", error);
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save program prices",
          message: `There was an issue saving the program prices. Please try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
        this.isApplyToAllClicked = false;
      }
    },
  },
};
</script>
